import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Col,
  Container,
  Input,
  Label,
  Progress,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import { Option } from "api/types/_public";
import { Rate } from "antd";
import {
  postBarReview,
  putBarReview,
} from "store/bar-review/thunk";
import UploadImageSingle from "components/Common/UploadImageSingle";
import UploadImage from "components/Common/UploadImage";

export interface Props {
  barID: string;
  id?: string;
  triggerRefresh?: () => void;
  isCloseModal?: any;
  rowSelected?: any;
  isEditModal?: boolean;
  LIST_OPTION_BAR_DETAIL_REVIEW_STAR_RATING_LANG?: Option[];
  setLinkImage?: any;
}

registerLocale("en", en);
registerLocale("ko", ko);

const ReviewForm = ({
  barID = "",
  triggerRefresh,
  isCloseModal,
  rowSelected,
  setLinkImage,
}: Props) => {
  const { t, i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);
  const [isFirstProgress, setIsFirstProgress] = useState<boolean>(true);

  const id = rowSelected?.id;

  const handleSubmit = async (values: any) => {
    try {
      isCloseModal && isCloseModal();

      setIsLoading((_prev) => true);

      const formatFile = values?.files?.length > 0 ? values?.files?.map((item: any) => item) : null;
      let data = { ...values, bar_id: barID, files: formatFile };

      if (id) delete data.bar_id;

      const response: any = id ? await putBarReview(id, data) : await postBarReview(data);

      if (response?.code === 200) {
        setIsLoading((_prev) => false);
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
        triggerRefresh && triggerRefresh();
      } else {
        setIsLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const validationSchema = Yup.object({
    reviewer_nickname: Yup.string().required(`${t("This field is required")}`),
    reviewer_email: Yup.string().email(`${t('Please enter a valid email address')}`).required(`${t("This field is required")}`),
    reviewer_avatar: Yup.string().nullable(),
    point: Yup.string().required(`${t("This field is required")}`),
    comment: Yup.string().required(`${t("This field is required")}`),
  });

  const formik = useFormik({
    initialValues: {
      comment: "",
      point: 3,
      reviewer_nickname: "",
      reviewer_email: "",
      reviewer_avatar: "",
      files: null
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const handleSetValueForm = (valueDefault: any) => {
    formik.setFieldValue("bar_id", valueDefault?.bar_id || "");
    formik.setFieldValue(
      "reviewer_nickname",
      valueDefault?.user_profile?.nickname || ""
    );
    formik.setFieldValue(
      "reviewer_email",
      valueDefault?.user_profile?.email || ""
    );
    formik.setFieldValue(
      "reviewer_avatar",
      valueDefault?.user_profile?.profile_image_url || ""
    );
    formik.setFieldValue("point", valueDefault?.point || "");
    formik.setFieldValue("comment", valueDefault?.comment || "");
    formik.setFieldValue("files", valueDefault?.files || null);
  };

  useEffect(() => {
    if (rowSelected) {
      handleSetValueForm(rowSelected);
    }
  }, [rowSelected]);

  useEffect(() => {
    document.title = `${t("Review")} - ${t("Bar Detail")} | Baive`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  useEffect(() => {
    if (Object.keys(formik.errors).length > 0) {
      setIsFirstProgress(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.keys(formik.errors).length])

  return (
    <React.Fragment>
      {isLoadingDetail && (
        <div
          style={{
            position: "absolute",
            zIndex: 3,
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: "rgb(164 164 164 / 36%)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spinner size="sm" color="primary"></Spinner>
        </div>
      )}
      <div>
        <Container fluid className="px-0">
          <div className="mb-4">
            <Progress
              value={
                ((!isFirstProgress || !!id) ? 100 - (100 / 5) * Number(Object.keys(formik.errors).length) : 0)
              }
              color="primary"
              className="animated-progress progress-sm"
            />
          </div>
          <form onSubmit={formik.handleSubmit}>
            <Row>
              <Col xxl={6}>
                <div className="mb-3">
                  <label className="form-label">
                    {t("Name")} <span className="text-danger"> *</span>
                  </label>
                  <Input
                    name="reviewer_nickname"
                    type="text"
                    className="form-control"
                    id="nameInput"
                    placeholder={t("Enter name")}
                    value={formik?.values?.reviewer_nickname}
                    onChange={(event: any) => {
                      formik.setFieldTouched("reviewer_nickname", true);
                      formik.setFieldValue(
                        "reviewer_nickname",
                        event?.target?.value
                      )
                    }}
                  />
                  {formik.touched.reviewer_nickname && formik.errors.reviewer_nickname ? (
                    <div className="text-danger mt-2">
                      {formik.errors.reviewer_nickname}
                    </div>
                  ) : null}
                </div>
              </Col>

              <Col xxl={6}>
                <div className="mb-3">
                  <label className="form-label">{t("Email")}</label> <span className="text-danger">*</span>
                  <Input
                    name="reviewer_email"
                    type="text"
                    className="form-control"
                    id="nameInput"
                    placeholder={t("Enter email")}
                    value={formik?.values?.reviewer_email}
                    onChange={(event: any) => {
                      formik.setFieldTouched("reviewer_email", true);
                      formik.setFieldValue(
                        "reviewer_email",
                        event?.target?.value
                      )
                    }}
                  />
                  {formik.touched.reviewer_email && formik.errors.reviewer_email ? (
                    <div className="text-danger mt-2">
                      {formik.errors.reviewer_email}
                    </div>
                  ) : null}
                </div>
              </Col>

              <Col lg={12}>
                <div className="mb-3">
                  <Label htmlFor="imageInput" className="form-label">
                    {t("Avatar")}
                  </Label>
                  <UploadImageSingle
                    data={formik.getFieldProps("reviewer_avatar")?.value}
                    setLinkImage={null}
                    formik={formik}
                    isMultiple={false}
                    uploadProps={{
                      name: "files",
                      accept: "image/png,image/jpeg,image/*",
                      beforeUpload: () => false,
                      headers: {
                        authorization: "authorization-text",
                      },
                    }}
                    formikNameImage={"reviewer_avatar"}
                  />
                </div>
              </Col>

              <Col xxl={12}>
                <div className="mb-3">
                  <Label className="form-label">{t("Star")}</Label>{" "}
                  <span className="text-danger"> *</span>
                  <div>
                    <Rate
                      onChange={(stars) => formik.setFieldValue("point", stars)}
                      value={formik.getFieldProps("point")?.value}
                    />
                  </div>
                </div>
              </Col>

              <Col xxl={12} className="date-picker-wrapper-custom mt-3 mt-md-2">
                <div className="mb-3">
                  <Label className="form-label">{`Comment`}</Label>{" "}
                  <span className="text-danger"> *</span>
                  <textarea
                    className="form-control"
                    id="comment"
                    name="comment"
                    value={formik?.values?.comment}
                    placeholder={`${t("Comment")}...`}
                    onChange={(event: any) => {
                      formik.setFieldTouched("reviewer_email", true);
                      formik.setFieldValue(
                        "comment",
                        event?.target?.value || ""
                      )
                    }}
                    style={{ height: 150 }}
                  />

                  {formik.touched.comment && formik.errors.comment ? (
                    <div className="text-danger mt-2">
                      {formik.errors.comment}
                    </div>
                  ) : null}
                </div>
              </Col>

              <Col lg={12}>
                <div className="mb-3">
                  <Label htmlFor="imageInput" className="form-label">
                    {t("Images")}
                  </Label>
                  <UploadImage
                    data={formik.getFieldProps("files")?.value}
                    setLinkImage={setLinkImage}
                    formik={formik}
                    isMultiple={true}
                    uploadProps={{
                      name: "files",
                      accept: "image/png,image/jpeg,image/*",
                      beforeUpload: () => false,
                      headers: {
                        authorization: "authorization-text",
                      },
                    }}
                    formikNameImage={"files"}
                    isListURLString={true}
                  />
                </div>
              </Col>

              <Col lg={12}>
                <div className="hstack gap-2 justify-content-end">
                  <button
                    className="btn btn-primary fs-14"
                    color="success"
                    type="submit"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <Spinner size="sm" className="me-2"></Spinner>
                    ) : (
                      <>{id ? t("Update Comment") : t("Create Comment")}</>
                    )}
                  </button>

                  {/* <button className="btn btn-soft-secondary fs-14" type="button" color="light" onClick={onCloseClick} disabled={isLoading}>
                        <i className="ri-indeterminate-circle-line align-bottom me-1"></i>
                        {t('Button Close')}
                    </button> */}
                </div>
              </Col>
            </Row>
          </form>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ReviewForm;
